'use client';

import { useState, useRef, useCallback, useEffect, HTMLAttributes } from 'react';
import IconVolumeHigh from '@/context/global/components/Icon/IconVolumeHigh';
import IconVolumeMute from '@/context/global/components/Icon/IconVolumeMute';
import { cn } from '@/context/global/utils/cn';

type VolumeControlProps = Omit<HTMLAttributes<HTMLDivElement>, 'onVolumeChange'> & {
  volume: number;
  onVolumeChange: (value: number) => void;
};

const VolumeControl = ({ volume, onVolumeChange, ...props }: VolumeControlProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [previousVolume, setPreviousVolume] = useState(1);
  const sliderRef = useRef<HTMLDivElement>(null);

  const isMuted = volume === 0;

  const handleToggleMute = () => {
    if (isMuted) {
      onVolumeChange(previousVolume > 0 ? previousVolume : 1);
    } else {
      setPreviousVolume(volume);
      onVolumeChange(0);
    }
  };

  const calculateVolume = useCallback(
    (clientY: number) => {
      const slider = sliderRef.current;
      if (!slider) return;

      const rect = slider.getBoundingClientRect();
      const clickY = clientY - rect.top;
      const newVolume = 1 - clickY / rect.height;
      const clampedVolume = Math.max(0, Math.min(1, newVolume));

      onVolumeChange(clampedVolume);
      if (clampedVolume > 0) {
        setPreviousVolume(clampedVolume);
      }
    },
    [onVolumeChange],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      setIsDragging(true);
      calculateVolume(e.clientY);
    },
    [calculateVolume],
  );

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      calculateVolume(e.clientY);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, calculateVolume]);

  return (
    <div
      {...props}
      className="relative flex flex-col items-center"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => !isDragging && setIsHovered(false)}
    >
      {/* Vertical Volume Slider - appears above button */}
      <div
        className={cn(
          'absolute bottom-full mb-2 overflow-hidden rounded-full border border-[#FFFFFF10] bg-[#00000080] p-2 transition-all duration-300',
          isHovered || isDragging ? 'h-[120px] opacity-100' : 'h-0 opacity-0',
        )}
      >
        <div
          ref={sliderRef}
          onMouseDown={handleMouseDown}
          className="relative h-[100px] w-3 cursor-pointer rounded-full bg-[#FFFFFF30]"
        >
          {/* Fill from bottom */}
          <div
            className="pointer-events-none absolute bottom-0 left-0 w-full rounded-full bg-[#A3D13C] transition-all"
            style={{ height: `${volume * 100}%` }}
          />
        </div>
      </div>

      {/* Volume Button */}
      <button
        onClick={handleToggleMute}
        className="flex size-[72px] transform cursor-pointer items-center justify-center rounded-full border border-[#FFFFFF10] bg-[#00000040] p-[15px] transition-all hover:scale-110"
      >
        {isMuted ? (
          <IconVolumeMute size={32} className="text-white" />
        ) : (
          <IconVolumeHigh size={32} className="text-white" />
        )}
      </button>
    </div>
  );
};

export default VolumeControl;
